import { sortBy } from 'remeda';
import {
  DEFAULT_BRANDING_VALUES,
  type PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import type { SerializedPlan } from 'src/pages/Settings/memberships/types';
import { serializeFeaturesForPayload } from 'src/pages/Settings/memberships/utils/serialize-features-config';
import { serializePlansConfig } from 'src/pages/Settings/memberships/utils/serialize-plans-config';
import { normalizeExternalUrl } from 'src/utils/url';

export type MembershipsConfigPayload = {
  partnerId: number;
  brandId: string;
  activationFlowConfigId: string | null;
  options: {
    showMonthlyPrices: boolean;
    showAnnualPrices: boolean;
    showSkipPriceSelection: boolean;
    requireTosBeforePlans: boolean;
    setupCheckoutText: string | null;
    showContactSupport: boolean;
    contactSupportLink: string | null;
    showFaq: boolean;
    faqQuestions: {
      question: string;
      answer: string;
      sortOrder: number;
    }[];
    branding: {
      primaryColor: string;
      secondaryColor: string;
      bannerColor: string;
      pageBlock1Color: string;
      backgroundImageUrl?: string | null;
    };
    termsOfServiceUrl: string | null;
    privacyPolicyUrl: string | null;
  };
  plans: SerializedPlan[];
  features: ReturnType<typeof serializeFeaturesForPayload>;
};

type SerializeSavePayloadInput = {
  partnerId: number;
  brandId: string;
  activationFlowConfigId?: string | null;
  formValues: PlansTabFormValues;
};

export const serializeMembershipsConfigPayload = ({
  partnerId,
  brandId,
  activationFlowConfigId = null,
  formValues,
}: SerializeSavePayloadInput): MembershipsConfigPayload => {
  const { billingOptions, plans } = serializePlansConfig(formValues);
  const faqQuestions = sortBy(
    formValues.options?.faqQuestions ?? [],
    (item) => item.sortOrder ?? 0,
  ).map((faqQuestion, index) => ({
    question: faqQuestion.question,
    answer: faqQuestion.answer,
    sortOrder: faqQuestion.sortOrder ?? index,
  }));

  const setupCheckoutTextValue = formValues.options?.setupCheckoutText?.trim();
  const setupCheckoutText = setupCheckoutTextValue || null;

  const contactSupportLink = formValues.options?.showContactSupport
    ? normalizeExternalUrl(formValues.options?.contactSupportLink ?? '')
    : null;

  const termsOfServiceUrl =
    normalizeExternalUrl(formValues.options?.termsOfServiceUrl ?? '') || null;
  const privacyPolicyUrl =
    normalizeExternalUrl(formValues.options?.privacyPolicyUrl ?? '') || null;

  const branding = formValues.options?.branding ?? DEFAULT_BRANDING_VALUES;

  return {
    partnerId,
    brandId,
    activationFlowConfigId,
    options: {
      showMonthlyPrices: billingOptions.monthlyEnabled,
      showAnnualPrices: billingOptions.annualEnabled,
      showSkipPriceSelection: formValues.options?.showSkipPriceSelection ?? false,
      requireTosBeforePlans: formValues.options?.requireTosBeforePlans ?? false,
      setupCheckoutText,
      showContactSupport: formValues.options?.showContactSupport ?? false,
      contactSupportLink,
      showFaq: formValues.options?.showFaq ?? false,
      faqQuestions,
      termsOfServiceUrl,
      privacyPolicyUrl,
      branding: {
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        bannerColor: branding.bannerColor,
        pageBlock1Color: branding.pageBlock1Color,
        backgroundImageUrl: branding.backgroundImageUrl?.trim() || null,
      },
    },
    plans,
    features: serializeFeaturesForPayload(formValues),
  };
};
